import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const LocationDetailsPanel = withListOverflow(LocationDetailsListOwner);
// src/features/locations/LocationDetailsPanel.jsx
//
// Slice L2 — Location Landing Header Rebalance.
// LP1.3 Gate 2 — extended with inline edit-mode rendering. When
// `editMode` is true the read-only InfoRow values become controlled
// inputs / selects driven by `draft` + `onDraftChange`. The set of
// editable fields is the 11-field REF panel order minus the always-
// read-only ID. Stage + Health stay editable via the identity-band
// pills (LP1.2 Gate 1) — they are NOT in this panel. The 5 unsurfaced
// editable fields (currency, district, floors, lat, lng) are tracked
// in tasks/todo.md as a Phase-2 backlog item; they keep their stored
// value across edit→save (buildDiffPayload sees no diff for them).
//
// Layering choice (intentional, do not "consolidate"):
//   • LocationDetailsPanel (this file, 11 fields) is the always-visible
//     quick reference shown in the page header. Now also the inline
//     edit surface for those 11 fields.
//   • BuildingPanel (in LocationDetail.jsx, 14 fields) is the deeper
//     view inside the Location tab — currently dead-code, preserved
//     for a future deep-detail view.
//
// Required-field markers (* after label) and per-field error rendering
// mirror the legacy EditLocationModal patterns. Region is a select
// (REGIONS enum); everything else is a text input. Numerics are text
// inputs because parseNumericField (called by buildDiffPayload) handles
// the string→number coercion uniformly for "12000" vs 12000.
//
// Read-only mode is unchanged from LP1.3 Gate 1 — passing no editMode
// prop renders the prior 11-field InfoRow layout exactly. Existing
// callers (and the LocationDetailsPanel test suite) need no updates.

import React from "react";
import { M_AND_A_TAGS, getMAndATagName } from "../../utils/mAndA.js";
import { fmtDate, formatFootprintSqftFull as formatSqftFull } from "../../utils/format.js";
import { T } from "../../app/tokens.js";
import { DateInput } from "../../components/DateInput.jsx";
import { REGIONS } from "../../app/tokens.js";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { closeDateVerb, normalizeScheduleDate } from "./capacityScheduleSummary.js";
import {
  getDisplayedSqft,
  isActiveLease,
  SQFT_OUTBOUND_TYPE,
  SQFT_OCCUPANT_TYPES,
} from "../../utils/locationSqftDerived.js";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { leaseTypeLabel } from "../leases/leaseTypes.js";

// Returns an ordered array of per-lease sqft contribution descriptors used
// by SqftBreakdownRow. Each item: { sign, size, label }.
// Only active leases with a concrete, finite size are included.
// Exported for isolated unit tests (principle C.3).
export function buildSqftBreakdown(leases) {
  if (!Array.isArray(leases)) return [];
  return leases
    .filter((l) => isActiveLease(l) && currentLeaseFootprint(l).grossSf != null
      && (SQFT_OCCUPANT_TYPES.has(l.type) || l.type === SQFT_OUTBOUND_TYPE))
    .map((l) => ({
      sign: l.type === SQFT_OUTBOUND_TYPE ? "\u2212" : "+",
      size: currentLeaseFootprint(l).grossSf,
      label: leaseTypeLabel(l.type) ?? l.type,
    }));
}

// Renders the Size row with an expandable per-lease contribution list.
// Used in both read-only and edit modes; showInfoIcon adds the ⓘ tooltip
// present in edit mode to signal that the value is derived (not editable).
function SqftBreakdownRow({ sizeText, breakdown, showInfoIcon, isEditRow, last }) {
  const [open, setOpen] = React.useState(false);
  const hasBreakdown = breakdown.length > 0;
  const rowPad = isEditRow ? "8px 0" : "13px 0";
  return (
    <div style={{ borderBottom: last ? "none" : `1px solid ${T.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: rowPad }}>
        <span
          data-testid="location-details-label-size"
          style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold, flexShrink: 0 }}
        >
          Size
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span
            data-testid="location-details-value-size"
            style={{ fontSize: T.fsBase, color: T.dark, fontWeight: T.fwSemibold, textAlign: "right" }}
          >
            {sizeText || "\u2014"}
          </span>
          {hasBreakdown && (
            <button
              type="button"
              data-testid="location-details-sqft-breakdown-toggle"
              aria-expanded={open}
              aria-label={open ? "Hide sqft breakdown" : "Show sqft breakdown"}
              onClick={() => setOpen((o) => !o)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.mid,
                fontSize: T.fsXs,
                padding: "2px 3px",
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {open ? "\u25be" : "\u25b8"}
            </button>
          )}
          {showInfoIcon && (
            <span
              data-testid="location-details-size-info-icon"
              title="Computed from active leases. Edit lease sizes individually via the Lease tab. For locations without an active lease, create a draft lease."
              aria-label="Computed from active leases. Edit lease sizes individually via the Lease tab. For locations without an active lease, create a draft lease."
              style={{ fontSize: T.fsSm, color: T.mid, cursor: "help", flexShrink: 0 }}
            >
              &#x24d8;
            </span>
          )}
        </div>
      </div>
      {open && hasBreakdown && (
        <div
          data-testid="location-details-sqft-breakdown"
          style={{ paddingBottom: 10, display: "flex", flexDirection: "column", gap: 3 }}
        >
          {breakdown.map((item, idx) => (
            <div
              key={idx}
              data-testid="location-details-sqft-breakdown-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                fontSize: T.fsXs,
                color: T.mid,
              }}
            >
              <span style={{ color: item.sign === "+" ? T.green : T.red, fontWeight: T.fwSemibold }}>
                {item.sign}{Math.round(item.size).toLocaleString()} sf
              </span>
              <span>\u2014 {item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// LP1 Gate 2 — InfoRow mirrors the legacy DetailCard row pattern
// (Ripple Workplace App.html:3245). Label-left/value-right flex with
// a 1px bottom divider on every row except the last. Muted ID variant
// (`muted` prop) renders the value in T.light per brief #4.
function InfoRow({ field, label, value, last, muted }) {
  const display = value == null || value === "" ? "—" : value;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        padding: "13px 0",
        borderBottom: last ? "none" : `1px solid ${T.border}`,
      }}
    >
      <span data-testid={field ? `location-details-label-${field}` : undefined} style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold, flexShrink: 0 }}>{label}</span>
      <span
        data-testid={field ? `location-details-value-${field}` : undefined}
        style={{
          fontSize: T.fsBase,
          color: muted ? T.light : T.dark,
          fontWeight: muted ? 400 : 600,
          textAlign: "right",
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {display}
      </span>
    </div>
  );
}

// LP1.3 Gate 2 — EditableRow keeps the InfoRow's label-left/control-
// right flex shape so the layout doesn't shift between read and edit
// modes. The right slot becomes a controlled <input> or <select>;
// errors render under the control in red. `required` adds the * marker
// and lets the Save handler's validate() flag missing values inline.
// LD2: `suffix` prop adds a non-interactive label inside the input's
// right edge (e.g. "sf" for the Size field). When set, the input's
// padding-right is widened so typed text cannot bleed under the label.
// HUB-LOCATION-S1: `boolean` type renders a yes/no checkbox with label.
function EditableRow({ field, label, type, value, error, required, options = [], suffix, hint, onChange, last }) {
  const inputStyle = {
    fontSize: T.fsBase,
    fontWeight: T.fwSemibold,
    color: T.dark,
    fontFamily: "inherit",
    padding: suffix ? "5px 28px 5px 8px" : "5px 8px",
    border: `1px solid ${error ? T.red : T.border}`,
    borderRadius: 4,
    background: T.card,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
  };
  const control =
    type === "boolean" ? (
      // Boolean toggle: checkbox + yes/no label. Uses a real <input type="checkbox">
      // for accessibility; the outer label makes the text clickable too.
      <label
        data-testid={`location-details-toggle-${field}`}
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      >
        <input
          type="checkbox"
          data-testid={`location-details-checkbox-${field}`}
          checked={Boolean(value)}
          onChange={(e) => onChange(field, e.target.checked)}
          style={{ width: 16, height: 16, cursor: "pointer", accentColor: T.blue }}
        />
        <span style={{ fontSize: T.fsBase, color: T.dark, fontWeight: T.fwSemibold }}>
          {Boolean(value) ? "Yes" : "No"}
        </span>
      </label>
    ) : type === "select" ? (
      <select
        data-testid={`location-details-select-${field}`}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        style={inputStyle}
      >
        <option value=""> - Select - </option>
        {options.map((opt) => {
          const val = typeof opt === "string" ? opt : opt.value;
          const lbl = typeof opt === "string" ? opt : opt.label;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    ) : type === "date" ? (
      // Date inputs need an explicit clear button — browsers don't expose
      // a reliable × affordance across all platforms. The button sets the
      // draft value to "" which buildDiffPayload maps to null on save.
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <DateInput
          data-testid={`location-details-input-${field}`}
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          style={{ ...inputStyle, flex: 1 }}
        />
        {value && (
          <button
            type="button"
            data-testid={`location-details-clear-${field}`}
            onClick={() => onChange(field, "")}
            title="Clear date"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: T.mid,
              fontSize: T.fsMd,
              lineHeight: 1,
              padding: "4px 2px",
              flexShrink: 0,
            }}
          >
            ×
          </button>
        )}
      </div>
    ) : (
      <input
        type={type}
        data-testid={`location-details-input-${field}`}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        style={inputStyle}
      />
    );
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        padding: "8px 0",
        borderBottom: last ? "none" : `1px solid ${T.border}`,
      }}
    >
      <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold, flexShrink: 0, paddingTop: 7 }}>
        {label}{required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}
      </span>
      <div style={{ flex: 1, minWidth: 0, maxWidth: "60%" }}>
        {suffix ? (
          <div style={{ position: "relative" }}>
            {control}
            <span
              data-testid={`location-details-suffix-${field}`}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: T.fsSm,
                color: T.mid,
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              {suffix}
            </span>
          </div>
        ) : control}
        {error && (
          <div
            data-testid={`location-details-error-${field}`}
            style={{ fontSize: T.fsXs, color: T.red, marginTop: 3 }}
          >
            {error}
          </div>
        )}
        {hint && (
          <div
            data-testid={`location-details-hint-${field}`}
            style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}
          >
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CapacityScheduleSection ─────────────────────────────────────────────────
//
// CAPACITY-SCHEDULE-S1: Renders the schedule table inside the edit panel.
// CAPACITY-SCHEDULE-S2: Extended with inline row editing.
// Props:
//   rows        — schedule rows from GET /capacity-schedule, ordered ASC
//   summary     — derived summary { onlineDate, todaySeats, nextChange, closeRow }
//   onAdd       — async (effectiveDate, installedSeats, maxDensity, note) → void
//   onRemove    — async (rowId) → void
//   addError    — string | null (validation error from last POST attempt)
//   removing    — Set<number> (rowIds currently being deleted)
//   readOnly    — if true, renders the read-only view (no add/remove controls)
//   onEdit      — async (rowId, patch) → void  [S2]
//   editing     — rowId | null (row currently in inline edit mode)  [S2]
//   onEditingChange — (rowId | null) → void (enter/cancel inline edit)  [S2]
//   editError   — string | null (last PATCH error)  [S2]
//   removeError — string | null (last DELETE error)  [S2]
function CapacityScheduleSection({
  rows = [],
  summary = null,
  onAdd,
  onRemove,
  addError,
  removing = new Set(),
  readOnly = false,
  onEdit = null,
  editing = null,
  onEditingChange = null,
  editError = null,
  removeError = null,
}) {
  // SCHEDULE-FUTURE-ROW-STYLING: rows dated after today get an "Upcoming" tag.
  const todayIso = new Date().toISOString().slice(0, 10);

  const [addOpen, setAddOpen] = React.useState(false);
  const [addDate, setAddDate] = React.useState("");
  const [addSeats, setAddSeats] = React.useState("");
  const [addMaxDensity, setAddMaxDensity] = React.useState("");
  const [addNote, setAddNote] = React.useState("");
  const [fieldError, setFieldError] = React.useState(null);

  // CAPACITY-SCHEDULE-S2: inline edit draft state (applies to the row whose
  // id === editing). Reset whenever editing changes.
  const [editDate, setEditDate] = React.useState("");
  const [editSeats, setEditSeats] = React.useState("");
  const [editMaxDensity, setEditMaxDensity] = React.useState("");
  const [editNote, setEditNote] = React.useState("");
  const [editFieldError, setEditFieldError] = React.useState(null);

  // When the editing row changes, seed the draft from the matching row.
  React.useEffect(() => {
    if (editing == null) {
      setEditDate("");
      setEditSeats("");
      setEditMaxDensity("");
      setEditNote("");
      setEditFieldError(null);
      return;
    }
    const row = rows.find((r) => r.id === editing);
    if (row) {
      setEditDate(row.effectiveDate ?? "");
      setEditSeats(row.installedSeats != null ? String(row.installedSeats) : "");
      setEditMaxDensity(row.maxDensity != null ? String(row.maxDensity) : "");
      setEditNote(row.note ?? "");
      setEditFieldError(null);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleEditSubmit(e, rowId) {
    e.preventDefault();
    if (!editDate) { setEditFieldError("Date is required"); return; }
    if (editSeats === "" || isNaN(Number(editSeats)) || Number(editSeats) < 0) {
      setEditFieldError("Installed seats must be a non-negative number");
      return;
    }
    setEditFieldError(null);
    onEdit(rowId, {
      effectiveDate:  editDate,
      installedSeats: Number(editSeats),
      maxDensity:     editMaxDensity !== "" ? Number(editMaxDensity) : null,
      note:           editNote || null,
    });
  }

  function handleAddSubmit(e) {
    e.preventDefault();
    if (!addDate) { setFieldError("Date is required"); return; }
    if (!addSeats || isNaN(Number(addSeats)) || Number(addSeats) < 0) {
      setFieldError("Installed seats must be a non-negative number");
      return;
    }
    setFieldError(null);
    onAdd(addDate, Number(addSeats), addMaxDensity ? Number(addMaxDensity) : null, addNote || null);
    setAddOpen(false);
    setAddDate("");
    setAddSeats("");
    setAddMaxDensity("");
    setAddNote("");
  }

  const thStyle = {
    fontSize: T.fsXxxs,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: T.mid,
    fontWeight: T.fwBold,
    paddingBottom: 4,
    textAlign: "left",
    borderBottom: `1px solid ${T.border}`,
    paddingRight: 8,
  };
  const tdStyle = {
    fontSize: T.fsXs,
    color: T.dark,
    padding: "6px 8px 6px 0",
    borderBottom: `1px solid ${T.lightGray}`,
    verticalAlign: "middle",
  };

  return (
    <div data-testid="capacity-schedule-section" style={{ marginBottom: 8 }}>
      {/* Summary strip */}
      {summary && (
        <div
          data-testid="capacity-schedule-summary"
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            fontSize: T.fsXs,
            color: T.mid,
            background: T.pageBg,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            padding: "6px 10px",
            marginBottom: 8,
          }}
        >
          {summary.onlineDate && (
            <span>
              <span style={{ color: T.light }}>Online: </span>
              <span data-testid="csched-summary-online" style={{ fontWeight: T.fwSemibold, color: T.dark }}>
                {summary.onlineDate}
              </span>
            </span>
          )}
          <span>
            <span style={{ color: T.light }}>Today: </span>
            <span data-testid="csched-summary-today" style={{ fontWeight: T.fwSemibold, color: T.dark }}>
              {summary.todaySeats} seats
            </span>
          </span>
          {summary.nextChange && (
            <span>
              <span style={{ color: T.light }}>Next change: </span>
              <span data-testid="csched-summary-next" style={{ fontWeight: T.fwSemibold, color: T.blue }}>
                {summary.nextChange.installedSeats} seats · {summary.nextChange.effectiveDate}
              </span>
            </span>
          )}
          {summary.closeRow && (
            <span>
              <span data-testid="csched-summary-close" style={{ fontWeight: T.fwSemibold, color: T.ragRed }}>
                {closeDateVerb(summary.closeRow)}{" "}
                {normalizeScheduleDate(summary.closeRow.effectiveDate) ?? summary.closeRow.effectiveDate}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Schedule table */}
      {rows.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: 8 }}>
          <table data-testid="capacity-schedule-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Seats</th>
                <th style={thStyle}>Max density</th>
                <th style={thStyle}>Note</th>
                {!readOnly && <th style={{ ...thStyle, width: 24 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = !readOnly && onEdit && editing === row.id;
                const isManagedLifecycleRow = row.source === "lifecycle_sync";
                if (isEditing) {
                  return (
                    <tr key={row.id} data-testid={`csched-row-${row.id}`}>
                      <td colSpan={5} style={{ ...tdStyle, paddingRight: 0 }}>
                        <form
                          data-testid={`csched-edit-form-${row.id}`}
                          onSubmit={(e) => handleEditSubmit(e, row.id)}
                          style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}
                        >
                          {(editFieldError || editError) && (
                            <div style={{ fontSize: T.fsXs, color: T.ragRed }}>{editFieldError ?? editError}</div>
                          )}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end" }}>
                            <div style={{ flex: "1 1 100px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Date *</div>
                              <DateInput
                                data-testid={`csched-edit-date-${row.id}`}
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
                              />
                            </div>
                            <div style={{ flex: "1 1 70px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Seats *</div>
                              <input
                                type="number"
                                min="0"
                                data-testid={`csched-edit-seats-${row.id}`}
                                value={editSeats}
                                onChange={(e) => setEditSeats(e.target.value)}
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
                              />
                            </div>
                            <div style={{ flex: "1 1 70px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Max density</div>
                              <input
                                type="number"
                                min="0"
                                data-testid={`csched-edit-max-density-${row.id}`}
                                value={editMaxDensity}
                                onChange={(e) => setEditMaxDensity(e.target.value)}
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
                              />
                            </div>
                            <div style={{ flex: "2 1 120px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Note</div>
                              <input
                                type="text"
                                data-testid={`csched-edit-note-${row.id}`}
                                value={editNote}
                                onChange={(e) => setEditNote(e.target.value)}
                                placeholder="optional"
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
                              />
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button
                                type="submit"
                                data-testid={`csched-edit-save-${row.id}`}
                                style={{
                                  padding: "4px 12px",
                                  fontSize: T.fsXs,
                                  fontWeight: T.fwSemibold,
                                  background: T.blue,
                                  color: T.white,
                                  border: "none",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                }}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                data-testid={`csched-edit-cancel-${row.id}`}
                                onClick={() => onEditingChange?.(null)}
                                style={{
                                  padding: "4px 10px",
                                  fontSize: T.fsXs,
                                  background: "none",
                                  border: `1px solid ${T.border}`,
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  color: T.mid,
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </form>
                      </td>
                    </tr>
                  );
                }
                const isFuture = row.effectiveDate > todayIso;
                return (
                  <tr
                    key={row.id}
                    data-testid={`csched-row-${row.id}`}
                    onClick={!readOnly && onEdit && !isManagedLifecycleRow
                      ? () => onEditingChange?.(row.id)
                      : undefined}
                    style={!readOnly && onEdit && !isManagedLifecycleRow
                      ? { cursor: "pointer" }
                      : undefined}
                  >
                    <td style={tdStyle}>
                      <span style={isFuture ? { color: T.mid } : undefined}>{row.effectiveDate}</span>
                      {isFuture && (
                        <span
                          data-testid={`csched-upcoming-${row.id}`}
                          style={{
                            marginLeft: 6,
                            background: T.pageBg,
                            color: T.blue,
                            border: `1px solid ${T.blue}`,
                            fontSize: T.fsXxxs,
                            fontWeight: T.fwSemibold,
                            padding: "1px 6px",
                            borderRadius: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Upcoming
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>{row.installedSeats}</td>
                    <td style={tdStyle}>{row.maxDensity ?? "—"}</td>
                    <td style={tdStyle}>{row.note ?? "—"}</td>
                    {!readOnly && (
                      <td style={{ ...tdStyle, width: 48, paddingRight: 0 }}>
                        <div style={{ display: "flex", gap: 2 }}>
                          {onEdit && !isManagedLifecycleRow && (
                            <button
                              type="button"
                              data-testid={`csched-edit-btn-${row.id}`}
                              onClick={(e) => { e.stopPropagation(); onEditingChange?.(row.id); }}
                              title="Edit row"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: T.mid,
                                fontSize: T.fsXs,
                                padding: "2px 4px",
                                lineHeight: 1,
                              }}
                            >
                              ✎
                            </button>
                          )}
                          {!isManagedLifecycleRow && (
                            <button
                              type="button"
                              data-testid={`csched-remove-${row.id}`}
                              disabled={removing.has(row.id)}
                              onClick={(e) => { e.stopPropagation(); onRemove(row.id); }}
                              title="Remove row"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: removing.has(row.id) ? T.light : T.ragRed,
                                fontSize: T.fsMd,
                                padding: "2px 4px",
                                lineHeight: 1,
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length === 0 && (
        <div style={{ fontSize: T.fsXs, color: T.light, fontStyle: "italic", marginBottom: 8 }}>
          No capacity schedule rows yet.
        </div>
      )}

      {removeError && (
        <div
          data-testid="csched-remove-error"
          style={{ fontSize: T.fsXs, color: T.ragRed, marginBottom: 8 }}
        >
          {removeError}
        </div>
      )}

      {/* Add form */}
      {!readOnly && !addOpen && (
        <button
          type="button"
          data-testid="csched-add-btn"
          onClick={() => setAddOpen(true)}
          style={{
            background: "none",
            border: `1px dashed ${T.border}`,
            borderRadius: 4,
            cursor: "pointer",
            color: T.blue,
            fontSize: T.fsXs,
            padding: "4px 10px",
            width: "100%",
          }}
        >
          + Add capacity change
        </button>
      )}

      {!readOnly && addOpen && (
        <form
          data-testid="csched-add-form"
          onSubmit={handleAddSubmit}
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            background: T.pageBg,
          }}
        >
          <div style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 2 }}>
            Add capacity change
          </div>
          {(fieldError || addError) && (
            <div style={{ fontSize: T.fsXs, color: T.ragRed }}>{fieldError ?? addError}</div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 100px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Effective date *</div>
              <DateInput
                data-testid="csched-add-date"
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
                style={{ width: "100%", fontSize: T.fsBase, padding: "4px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: "1 1 80px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Installed seats *</div>
              <input
                type="number"
                min="0"
                data-testid="csched-add-seats"
                value={addSeats}
                onChange={(e) => setAddSeats(e.target.value)}
                style={{ width: "100%", fontSize: T.fsBase, padding: "4px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: "1 1 80px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Max density</div>
              <input
                type="number"
                min="0"
                data-testid="csched-add-max-density"
                value={addMaxDensity}
                onChange={(e) => setAddMaxDensity(e.target.value)}
                style={{ width: "100%", fontSize: T.fsBase, padding: "4px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: "2 1 160px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Note</div>
              <input
                type="text"
                data-testid="csched-add-note"
                value={addNote}
                onChange={(e) => setAddNote(e.target.value)}
                placeholder="optional"
                style={{ width: "100%", fontSize: T.fsBase, padding: "4px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="submit"
              data-testid="csched-add-save"
              style={{
                padding: "5px 14px",
                fontSize: T.fsXs,
                fontWeight: T.fwSemibold,
                background: T.blue,
                color: T.white,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              Add
            </button>
            <button
              type="button"
              data-testid="csched-add-cancel"
              onClick={() => { setAddOpen(false); setFieldError(null); }}
              style={{
                padding: "5px 14px",
                fontSize: T.fsXs,
                background: "none",
                border: `1px solid ${T.border}`,
                borderRadius: 4,
                cursor: "pointer",
                color: T.mid,
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── HeadcountScheduleSection ────────────────────────────────────────────────
//
// OFFICE-HEADCOUNT-MANUAL-OVERRIDE: Renders the headcount schedule table inside
// the location details panel.  Mirrors CapacityScheduleSection in form and function.
//
// Props:
//   rows        — schedule rows from GET /headcount-schedule (ordered DESC)
//   onAdd       — async (effectiveDate, totalCount, note) → void
//   onRemove    — async (rowId) → void
//   addError    — string | null (validation error from last POST attempt)
//   removing    — Set<number> (rowIds currently being deleted)
//   readOnly    — if true, renders the read-only view (no add/remove controls)
//   onEdit      — async (rowId, patch) → void
//   editing     — rowId | null (row currently in inline edit mode)
//   onEditingChange — (rowId | null) → void (enter/cancel inline edit)
//   editError   — string | null (last PATCH error)
//   removeError — string | null (last DELETE error)
//
// Source badge: 'manual' → blue pill, 'workday' → muted uppercase label
function HeadcountScheduleSection({
  rows = [],
  onAdd,
  onRemove,
  addError,
  addWarning = null,
  removing = new Set(),
  readOnly = false,
  onEdit = null,
  editing = null,
  onEditingChange = null,
  editError = null,
  removeError = null,
}) {

  // SCHEDULE-FUTURE-ROW-STYLING: rows dated after today get an "Upcoming" tag.
  const todayIso = new Date().toISOString().slice(0, 10);

  const [addOpen, setAddOpen] = React.useState(false);
  const [addDate, setAddDate] = React.useState("");
  const [addCount, setAddCount] = React.useState("");
  const [addNote, setAddNote] = React.useState("");
  const [addSplit, setAddSplit] = React.useState({
    fullTimeCount: "",
    vendorContractorCount: "",
    independentContractorCount: "",
  });
  const [fieldError, setFieldError] = React.useState(null);

  const [editDate, setEditDate] = React.useState("");
  const [editCount, setEditCount] = React.useState("");
  const [editNote, setEditNote] = React.useState("");
  const [editSplit, setEditSplit] = React.useState({
    fullTimeCount: "",
    vendorContractorCount: "",
    independentContractorCount: "",
  });
  const [editFieldError, setEditFieldError] = React.useState(null);
  const splitInputs = [
    ["fullTimeCount", "Full-time"],
    ["vendorContractorCount", "Vendor contractors"],
    ["independentContractorCount", "Independent contractors"],
  ];

  function parseSplitFields(values) {
    const parsed = {};
    for (const [field] of splitInputs) {
      const value = values[field];
      if (value === "") {
        parsed[field] = null;
        continue;
      }
      const count = Number(value);
      if (!Number.isInteger(count) || count < 0) return null;
      parsed[field] = count;
    }
    return parsed;
  }

  React.useEffect(() => {
    if (editing == null) {
      setEditDate("");
      setEditCount("");
      setEditNote("");
      setEditSplit({
        fullTimeCount: "",
        vendorContractorCount: "",
        independentContractorCount: "",
      });
      setEditFieldError(null);
      return;
    }
    const row = rows.find((r) => r.id === editing);
    if (row) {
      setEditDate(row.effectiveDate ?? "");
      setEditCount(row.totalCount != null ? String(row.totalCount) : "");
      setEditNote(row.note ?? "");
      setEditSplit({
        fullTimeCount: row.fullTimeCount == null ? "" : String(row.fullTimeCount),
        vendorContractorCount: row.vendorContractorCount == null ? "" : String(row.vendorContractorCount),
        independentContractorCount: row.independentContractorCount == null
          ? ""
          : String(row.independentContractorCount),
      });
      setEditFieldError(null);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleEditSubmit(e, rowId) {
    e.preventDefault();
    if (!editDate) { setEditFieldError("Date is required"); return; }
    if (editCount === "" || isNaN(Number(editCount)) || Number(editCount) < 0) {
      setEditFieldError("Headcount must be a non-negative number"); return;
    }
    const split = parseSplitFields(editSplit);
    if (!split) { setEditFieldError("Worker-type counts must be non-negative whole numbers"); return; }
    setEditFieldError(null);
    onEdit(rowId, {
      effectiveDate: editDate,
      totalCount: Number(editCount),
      note: editNote || null,
      ...split,
    });
  }

  function handleAddSubmit(e) {
    e.preventDefault();
    if (!addDate) { setFieldError("Date is required"); return; }
    if (!addCount || isNaN(Number(addCount)) || Number(addCount) < 0) {
      setFieldError("Headcount must be a non-negative number"); return;
    }
    const split = parseSplitFields(addSplit);
    if (!split) { setFieldError("Worker-type counts must be non-negative whole numbers"); return; }
    setFieldError(null);
    onAdd(addDate, Number(addCount), addNote || null, split);
    setAddOpen(false);
    setAddDate("");
    setAddCount("");
    setAddNote("");
    setAddSplit({
      fullTimeCount: "",
      vendorContractorCount: "",
      independentContractorCount: "",
    });
  }

  const thStyle = {
    fontSize: T.fsXxxs, textTransform: "uppercase", letterSpacing: "0.05em",
    color: T.mid, fontWeight: T.fwBold, paddingBottom: 4, textAlign: "left",
    borderBottom: `1px solid ${T.border}`, paddingRight: 8,
  };
  const tdStyle = {
    fontSize: T.fsXs, color: T.dark, padding: "6px 8px 6px 0",
    borderBottom: `1px solid ${T.lightGray}`, verticalAlign: "middle",
  };

  return (
    <div data-testid="hcsched-section" style={{ marginBottom: 8 }}>
      {rows.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: 8 }}>
          <table data-testid="hcsched-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Headcount</th>
                <th style={thStyle}>FT / vendor / independent</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Note</th>
                {!readOnly && <th style={{ ...thStyle, width: 24 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = !readOnly && onEdit && editing === row.id;
                const displayCount = row.totalCount != null ? row.totalCount : "--";
                if (isEditing) {
                  return (
                    <tr key={row.id} data-testid={`hcsched-row-${row.id}`}>
                      <td colSpan={6} style={{ ...tdStyle, paddingRight: 0 }}>
                        <form
                          data-testid={`hcsched-edit-form-${row.id}`}
                          onSubmit={(e) => handleEditSubmit(e, row.id)}
                          style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}
                        >
                          {(editFieldError || editError) && (
                            <div style={{ fontSize: T.fsXs, color: T.ragRed }}>{editFieldError ?? editError}</div>
                          )}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end" }}>
                            <div style={{ flex: "1 1 100px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Date *</div>
                              <DateInput data-testid={`hcsched-edit-date-${row.id}`}
                                value={editDate} onChange={(e) => setEditDate(e.target.value)}
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }} />
                            </div>
                            <div style={{ flex: "1 1 80px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Headcount *</div>
                              <input type="number" min="0" data-testid={`hcsched-edit-count-${row.id}`}
                                value={editCount} onChange={(e) => setEditCount(e.target.value)}
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }} />
                            </div>
                            {splitInputs.map(([field, label]) => (
                              <div key={field} style={{ flex: "1 1 90px" }}>
                                <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>{label}</div>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  data-testid={`hcsched-edit-${field}-${row.id}`}
                                  value={editSplit[field]}
                                  onChange={(e) => setEditSplit((current) => ({
                                    ...current,
                                    [field]: e.target.value,
                                  }))}
                                  placeholder="optional"
                                  style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
                                />
                              </div>
                            ))}
                            <div style={{ flex: "2 1 120px" }}>
                              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Note</div>
                              <input type="text" data-testid={`hcsched-edit-note-${row.id}`}
                                value={editNote} onChange={(e) => setEditNote(e.target.value)}
                                placeholder="optional"
                                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }} />
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button type="submit" data-testid={`hcsched-edit-save-${row.id}`}
                                style={{ padding: "4px 12px", fontSize: T.fsXs, fontWeight: T.fwSemibold, background: T.blue, color: T.white, border: "none", borderRadius: 4, cursor: "pointer" }}>
                                Save
                              </button>
                              <button type="button" data-testid={`hcsched-edit-cancel-${row.id}`}
                                onClick={() => onEditingChange?.(null)}
                                style={{ padding: "4px 10px", fontSize: T.fsXs, background: "none", border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer", color: T.mid }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </form>
                      </td>
                    </tr>
                  );
                }
                const isFuture = row.effectiveDate > todayIso;
                return (
                  <tr key={row.id} data-testid={`hcsched-row-${row.id}`}
                    onClick={!readOnly && onEdit ? () => onEditingChange?.(row.id) : undefined}
                    style={!readOnly && onEdit ? { cursor: "pointer" } : undefined}>
                    <td style={tdStyle}>
                      <span style={isFuture ? { color: T.mid } : undefined}>{row.effectiveDate}</span>
                      {isFuture && (
                        <span
                          data-testid={`hcsched-upcoming-${row.id}`}
                          style={{
                            marginLeft: 6,
                            background: T.pageBg,
                            color: T.blue,
                            border: `1px solid ${T.blue}`,
                            fontSize: T.fsXxxs,
                            fontWeight: T.fwSemibold,
                            padding: "1px 6px",
                            borderRadius: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Upcoming
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>{displayCount}</td>
                    <td style={tdStyle}>
                      {[row.fullTimeCount, row.vendorContractorCount, row.independentContractorCount]
                        .map((count) => count ?? "—")
                        .join(" / ")}
                    </td>
                    <td style={tdStyle}>
                      {row.source === "manual" ? (
                        <span style={{ background: T.blue, color: T.white, fontSize: T.fsXxxs, fontWeight: T.fwSemibold, padding: "1px 6px", borderRadius: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Manual
                        </span>
                      ) : (
                        <span style={{ color: T.mid, fontSize: T.fsXxxs, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Workday
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>{row.note ?? "—"}</td>
                    {!readOnly && (
                      <td style={{ ...tdStyle, width: 48, paddingRight: 0 }}>
                        <div style={{ display: "flex", gap: 2 }}>
                          {onEdit && (
                            <button type="button" data-testid={`hcsched-edit-btn-${row.id}`}
                              onClick={(e) => { e.stopPropagation(); onEditingChange?.(row.id); }}
                              title="Edit row"
                              style={{ background: "none", border: "none", cursor: "pointer", color: T.mid, fontSize: T.fsXs, padding: "2px 4px", lineHeight: 1 }}>
                              ✎
                            </button>
                          )}
                          <button type="button" data-testid={`hcsched-remove-${row.id}`}
                            disabled={removing.has(row.id)}
                            onClick={(e) => { e.stopPropagation(); onRemove(row.id); }}
                            title="Remove row"
                            style={{ background: "none", border: "none", cursor: "pointer", color: removing.has(row.id) ? T.light : T.ragRed, fontSize: T.fsMd, padding: "2px 4px", lineHeight: 1 }}>
                            ×
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length === 0 && (
        <div style={{ fontSize: T.fsXs, color: T.light, fontStyle: "italic", marginBottom: 8 }}>
          No headcount schedule rows yet.
        </div>
      )}
      {removeError && (
        <div style={{ fontSize: T.fsXs, color: T.ragRed, marginBottom: 4 }}>{removeError}</div>
      )}
      {addWarning && (
        <div
          data-testid="hcsched-add-warning"
          style={{
            fontSize: T.fsXs,
            color: T.blockedText,
            background: T.warnAmberBg,
            border: `1px solid ${T.warnAmberBorder}`,
            borderRadius: 4,
            padding: "4px 8px",
            marginBottom: 4,
          }}
        >
          ⚠ {addWarning}
        </div>
      )}
      {!readOnly && onAdd && !addOpen && (
        <button type="button" data-testid="hcsched-add-btn"
          onClick={() => setAddOpen(true)}
          style={{ fontSize: T.fsXs, color: T.blue, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
          + Add row
        </button>
      )}
      {!readOnly && addOpen && (
        <form data-testid="hcsched-add-form" onSubmit={handleAddSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 0" }}>
          {(fieldError || addError) && (
            <div style={{ fontSize: T.fsXs, color: T.ragRed }}>{fieldError ?? addError}</div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 100px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Date *</div>
              <DateInput data-testid="hcsched-add-date"
                value={addDate} onChange={(e) => setAddDate(e.target.value)}
                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: "1 1 80px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Headcount *</div>
              <input type="number" min="0" data-testid="hcsched-add-count"
                value={addCount} onChange={(e) => setAddCount(e.target.value)}
                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }} />
            </div>
            {splitInputs.map(([field, label]) => (
              <div key={field} style={{ flex: "1 1 90px" }}>
                <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>{label}</div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  data-testid={`hcsched-add-${field}`}
                  value={addSplit[field]}
                  onChange={(e) => setAddSplit((current) => ({
                    ...current,
                    [field]: e.target.value,
                  }))}
                  placeholder="optional"
                  style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }}
                />
              </div>
            ))}
            <div style={{ flex: "2 1 120px" }}>
              <div style={{ fontSize: T.fsXxxs, color: T.mid, marginBottom: 2 }}>Note</div>
              <input type="text" data-testid="hcsched-add-note"
                value={addNote} onChange={(e) => setAddNote(e.target.value)}
                placeholder="optional"
                style={{ width: "100%", fontSize: T.fsBase, padding: "3px 6px", border: `1px solid ${T.border}`, borderRadius: 4, boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button type="submit" data-testid="hcsched-add-submit"
                style={{ padding: "4px 12px", fontSize: T.fsXs, fontWeight: T.fwSemibold, background: T.blue, color: T.white, border: "none", borderRadius: 4, cursor: "pointer" }}>
                Add
              </button>
              <button type="button" data-testid="hcsched-add-cancel"
                onClick={() => {
                  setAddOpen(false);
                  setAddDate("");
                  setAddCount("");
                  setAddNote("");
                  setAddSplit({
                    fullTimeCount: "",
                    vendorContractorCount: "",
                    independentContractorCount: "",
                  });
                  setFieldError(null);
                }}
                style={{ padding: "4px 10px", fontSize: T.fsXs, background: "none", border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer", color: T.mid }}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

// LP1.3 Gate 2 — props extended with editMode + draft + errors +
// onDraftChange. When editMode is false (the default) the panel
// renders exactly as it did in LP1.3 Gate 1 — read-only InfoRows.
// When editMode is true:
//   - draft must be a buildPrefill() result (every editable field is
//     a string). The panel reads from draft (not location) so the
//     user's typed values stay visible.
//   - onDraftChange(field, value) is called for every keystroke.
//   - errors is a { [field]: "Required." } map populated by the
//     parent's validate() on Save.
//   - ID stays read-only display (immutable PK).
//   - Address loses its Maps link (it'd compete with the input).
//   - Size is read-only in edit mode (LSE-SQFT-SINGLE-SOURCE): derived
//     from active leases. An info icon tooltip directs users to the
//     Lease tab to edit lease sizes instead.
//
// CAPACITY-SCHEDULE-S1: optional `capacitySchedule` prop. When provided,
// the edit panel shows the CapacityScheduleSection in place of the five
// flat fields (installedCapacity, maxCapacity, onlineDate, closeDate,
// plannedSeats). The parent (LocationDetail) is responsible for fetching
// the schedule and passing CRUD callbacks. When not provided, the existing
// flat EditableRow fields are shown unchanged (backward compat for callers
// not yet wired to the schedule endpoint).
function LocationDetailsListOwner({
  location,
  editMode = false,
  draft = null,
  errors = {},
  onDraftChange = () => {},
  // CAPACITY-SCHEDULE-S1: optional schedule section props.
  // When provided, replaces the 5 flat capacity/date fields in edit mode
  // and shows the schedule table in read-only mode for multi-row offices.
  capacitySchedule = null,
  // onCapacityScheduleAdd(effectiveDate, installedSeats, maxDensity, note)
  onCapacityScheduleAdd = null,
  // onCapacityScheduleRemove(rowId)
  onCapacityScheduleRemove = null,
  capacityScheduleAddError = null,
  capacityScheduleRemoving = null,
  // CAPACITY-SCHEDULE-S2: inline edit props.
  onCapacityScheduleEdit          = null,
  capacityScheduleEditing         = null,
  onCapacityScheduleEditingChange = null,
  capacityScheduleEditError       = null,
  capacityScheduleRemoveError     = null,
  // OFFICE-HEADCOUNT-MANUAL-OVERRIDE: optional headcount schedule section props.
  // When provided, the panel shows a HeadcountScheduleSection in both edit and
  // read-only mode. The parent (LocationDetail) is responsible for fetching the
  // schedule and passing CRUD callbacks.
  headcountSchedule               = null,
  onHeadcountScheduleAdd          = null,
  onHeadcountScheduleRemove       = null,
  headcountScheduleAddError       = null,
  // RETROACTIVE-WRITES: advisory warning from server when a row is written after
  // the office's vacate/exit date. Set by the parent after save flush.
  headcountScheduleAddWarning     = null,
  headcountScheduleRemoving       = null,
  onHeadcountScheduleEdit         = null,
  headcountScheduleEditing        = null,
  onHeadcountScheduleEditingChange = null,
  headcountScheduleEditError      = null,
  headcountScheduleRemoveError    = null,
}) {
  // Defensive: edit mode was requested but no draft passed yet — fall
  // back to read-only render rather than crashing on draft.market.
  const inEdit = editMode && draft != null;

  // LSE-SQFT-SINGLE-SOURCE: size is always derived via the canonical
  // helper so active leases take precedence over stored location.sqft.
  // Empty string (not "—") when there is no data so InfoRow shows "—".
  const derivedSqft = getDisplayedSqft(location, null);
  const sizeText = derivedSqft != null ? formatSqftFull(derivedSqft) : "";

  // Task-875 — per-lease contribution breakdown for the Size row toggle.
  // Computed from the detail payload's embedded leases array; falls back
  // to an empty array when location.leases is absent (list-payload objects).
  const sqftBreakdown = buildSqftBreakdown(location.leases);

  // Address renders as an external Google Maps link when present (read-only).
  const addressNode = location.address ? (
    <a
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: T.dark, textDecoration: "none", fontWeight: T.fwSemibold }}
      data-testid="location-details-address-link"
    >
      {location.address}
      <span style={{ marginLeft: 6, color: T.mid, fontSize: T.fsXs }} aria-hidden="true">↗</span>
    </a>
  ) : null;

  return (
    <div data-testid="location-details-panel" style={{ border: `1px solid ${T.border}`, borderRadius: 10 }}>
      {/* LP1 Gate 1 — sub-header with PaneHeading (3×14 accent bar, 14/700/T.dark). */}
      {/* C7 — minHeight 50 + marginBottom 12 unify this sub-header with
          PhotoPanel's so the first row top edge aligns with the photo
          image top edge across the two-column header. The Photo card's
          toggle makes its sub-header naturally taller (~50px) and used
          marginBottom 12 already; this side raises from marginBottom 4
          → 12 and adds matching minHeight 50 so its single-line title
          centers vertically inside the same box. Kept symmetric with
          PhotoPanel — see that file's C7 comment for the DRY-refactor
          note. */}
      {/* LD1 — header left-pad 16px + accent (3px) + gap (8px) = title
          at 27px from panel edge. Row labels sit at "0 20px" (20px);
          the visual offset is intentional — the accent bar anchors
          the left edge. */}
      <div
        style={{
          padding: "16px 20px 16px 16px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          // C7 — minHeight 50 matches PhotoPanel's compressed header height.
          // PhotoPanel header: 11px pad + 28px toggle (2+24+2) + 11px pad = 50px.
          // Both headers align at 50px; title text centers at ~17px from each edge.
          minHeight: 50,
          marginBottom: 12,
        }}
        data-testid="location-details-header"
      >
        <PaneHeading
          barProps={{ "data-testid": "location-details-accent" }}
          style={{ marginBottom: 0 }}
          role="heading"
          aria-level={3}
        >
          Office Details
        </PaneHeading>
      </div>
      <div style={{ padding: "0 20px" }} data-testid="location-details-rows">
        {inEdit ? (
          <>
            <EditableRow field="market" label="Market" type="text" required
              value={draft.market} error={errors.market} onChange={onDraftChange} />
            <EditableRow field="locationAlias" label="Office Alias" type="text"
              value={draft.locationAlias} error={errors.locationAlias} onChange={onDraftChange} />
            <EditableRow field="address" label="Address" type="text" required
              value={draft.address} error={errors.address} onChange={onDraftChange} />
            <EditableRow field="suite" label="Premises Description" type="text"
              value={draft.suite} error={errors.suite} onChange={onDraftChange} />
            <EditableRow field="country" label="Country" type="text" required
              value={draft.country} error={errors.country} onChange={onDraftChange} />
            <EditableRow field="region" label="Region" type="select" required
              options={REGIONS}
              value={draft.region} error={errors.region} onChange={onDraftChange} />
            {/* HUB-LOCATION-REORDER: Hub Location now sits directly after Region
                and before Size, in both the with-schedule and without-schedule
                branches below. */}
            <EditableRow field="isHub" label="Hub Location" type="boolean"
              value={draft.isHub} error={errors.isHub} onChange={onDraftChange} />
            {/* LSE-SQFT-SINGLE-SOURCE: Size is read-only in edit mode —
                derived from active leases. Users edit lease sizes on the
                Lease tab; the tooltip explains the derivation path.
                Task-875: SqftBreakdownRow adds the expandable per-lease list. */}
            <SqftBreakdownRow
              sizeText={sizeText}
              breakdown={sqftBreakdown}
              showInfoIcon
              isEditRow
            />
             {/* The physical lifecycle controls stay outside the capacity
                 schedule branch: they are location fields, not schedule-row
                 editors, and must remain visible when schedule data is loaded. */}
             <EditableRow field="onlineDate" label="FDOB / Open date" type="date"
               hint="When this office goes/went live - drives the build-to-operational transition."
               value={draft.onlineDate} error={errors.onlineDate} onChange={onDraftChange} />
             <EditableRow field="exitDate" label="Exit / close date" type="date"
               hint="Canonical physical closure date. Lease expiry is tracked separately on the Lease tab."
               value={draft.exitDate} error={errors.exitDate} onChange={onDraftChange} />
             <EditableRow field="reopenDate" label="Reopen date" type="date"
               hint="Optional date when this office becomes physically available again."
               value={draft.reopenDate} error={errors.reopenDate} onChange={onDraftChange} />
             {/* CAPACITY-SCHEDULE-S1: When capacitySchedule is provided the
                 capacity fields are replaced by the schedule section. */}
            {capacitySchedule != null ? (
              <>
                 {/* LD1 — mAndA stays before the schedule section so field
                     ordering remains Market…Hub…M&A → lifecycle → Schedule. */}
                <EditableRow field="mAndA" label="M&A" type="select"
                  options={M_AND_A_TAGS.map((tag) => ({ value: tag.id, label: tag.name }))}
                  value={draft.mAndA} error={errors.mAndA} onChange={onDraftChange} />
                <div
                  data-testid="location-details-csched-section-wrapper"
                  style={{
                    padding: "10px 0",
                    borderBottom: `1px solid ${T.border}`,
                  }}
                >
                  <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: "0.06em", color: T.mid, marginBottom: 8 }}>
                    Capacity Schedule
                  </div>
                  {/* No read-only twin for CapacityScheduleSection — this is
                      the only render site; it is already inside the inEdit
                      branch so it never appears in read-only mode. */}
                  <CapacityScheduleSection
                    rows={capacitySchedule.rows ?? []}
                    summary={capacitySchedule.summary ?? null}
                    onAdd={onCapacityScheduleAdd}
                    onRemove={onCapacityScheduleRemove}
                    addError={capacityScheduleAddError}
                    removing={capacityScheduleRemoving ?? new Set()}
                    onEdit={onCapacityScheduleEdit}
                    editing={capacityScheduleEditing}
                    onEditingChange={onCapacityScheduleEditingChange}
                    editError={capacityScheduleEditError}
                    removeError={capacityScheduleRemoveError}
                  />
                </div>
              </>
            ) : (
              <>
                <EditableRow field="installedCapacity" label="Installed Capacity" type="text"
                  value={draft.installedCapacity} error={errors.installedCapacity} onChange={onDraftChange} />
                <EditableRow field="maxCapacity" label="Maximum Density" type="text"
                  hint="Most desks the space can physically hold (densification ceiling) - drives Space Fit's Expandable verdict."
                  value={draft.maxCapacity} error={errors.maxCapacity} onChange={onDraftChange} />
                {/* LD1 — mAndA was last; mAndA loses `last` prop.
                    FDOB-TO-ONLINEDATE-CONSOLIDATE-S1: FDOB row removed — Online
                    Date (below) is now the single field for "went/goes live". */}
                <EditableRow field="mAndA" label="M&A" type="select"
                  options={M_AND_A_TAGS.map((tag) => ({ value: tag.id, label: tag.name }))}
                  value={draft.mAndA} error={errors.mAndA} onChange={onDraftChange} />
                <EditableRow field="plannedSeats" label="Planned Seats" type="text"
                  hint="Plan-of-record target seat count - distinct from Installed Capacity."
                  value={draft.plannedSeats} error={errors.plannedSeats} onChange={onDraftChange} last />
              </>
            )}
            {headcountSchedule != null && (
              <div
                data-testid="location-details-hcsched-section-wrapper"
                style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}` }}
              >
                <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: "0.06em", color: T.mid, marginBottom: 8 }}>
                  Headcount Schedule
                </div>
                <HeadcountScheduleSection
                  rows={headcountSchedule.rows ?? []}
                  onAdd={onHeadcountScheduleAdd}
                  onRemove={onHeadcountScheduleRemove}
                  addError={headcountScheduleAddError}
                  addWarning={headcountScheduleAddWarning}
                  removing={headcountScheduleRemoving ?? new Set()}
                  onEdit={onHeadcountScheduleEdit}
                  editing={headcountScheduleEditing}
                  onEditingChange={onHeadcountScheduleEditingChange}
                  editError={headcountScheduleEditError}
                  removeError={headcountScheduleRemoveError}
                />
              </div>
            )}
          </>
        ) : (
          // LP1.4 — read-only field-set narrowing. The 11-field REF
          // order is preserved in EDIT mode (above); in READ-ONLY mode
          // we surface only the essential fields, plus Premises Description
          // when populated. Three deliberate omissions:
          //
          //   • Market — dropped in LD1 (surfaced in identity bar)
          //   • Location Alias — internal label, not customer-facing
          //   • Country — implied by Market for an internal portfolio
          //   • Region — implied by Market; surfaced via header pills
          //
          // Premises Description hides on null/empty — it's the one optional
          // field where "no data" is meaningful (not every site has
          // a suite). The other essential fields use em-dash on null
          // because "needs data" is a useful signal. Do NOT normalize
          // this to all-em-dash or all-hide — see LP1.4 brief Risk flags.
          //
          // LD1 — M&A hides entirely when null/empty (no em-dash row).
          // ID reshaped to inline footer below the rows container.
          <>
            <InfoRow field="address" label="Address" value={addressNode} />
            {location.suite != null && location.suite !== "" && (
              <InfoRow field="suite" label="Premises Description" value={location.suite} />
            )}
            <SqftBreakdownRow sizeText={sizeText} breakdown={sqftBreakdown} />
            {/* SPACE-FIT-MAXCAP-ADMIN: installed-capacity moved inside IIFE so
                its `last` prop can reflect whether max-capacity (ui_v2-gated)
                is present or absent below it. */}
            {(() => {
              const hasMAndA = location.mAndA != null && location.mAndA !== "";
              // PLAN-TIMELINE-COVERAGE-S1: only shown once set — these are new
              // fields most existing locations won't have yet, and an em-dash
              // row for every one of them would clutter the read-only panel.
              // FDOB-TO-ONLINEDATE-CONSOLIDATE-S1: hasFdob/FDOB row removed —
              // Online Date (below) is the single "went/goes live" field now.
              const hasOnlineDate = location.onlineDate != null && location.onlineDate !== "";
               const exitDate = location.exitDate ?? location.closeDate;
               const hasExitDate = exitDate != null && exitDate !== "";
               const hasReopenDate = location.reopenDate != null && location.reopenDate !== "";
              const hasPlannedSeats = location.plannedSeats != null;
               const isLastRow = !hasMAndA && !hasOnlineDate && !hasExitDate && !hasReopenDate && !hasPlannedSeats;
              return (
                <>
                  <InfoRow field="installed-capacity" label="Installed Capacity" value={location.installedCapacity} />
                  <InfoRow field="max-capacity" label="Maximum Density" value={location.maxCapacity}
                    last={isLastRow} />
                  {hasMAndA && (
                    <InfoRow field="m-and-a" label="M&A" value={getMAndATagName(location.mAndA)}
                       last={!hasOnlineDate && !hasExitDate && !hasReopenDate && !hasPlannedSeats} />
                  )}
                  {hasOnlineDate && (
                    <InfoRow field="online-date" label="Online Date" value={fmtDate(location.onlineDate)}
                      last={!hasExitDate && !hasReopenDate && !hasPlannedSeats} />
                  )}
                   {hasExitDate && (
                     <InfoRow field="exit-date" label="Exit / close date" value={fmtDate(exitDate)}
                       last={!hasReopenDate && !hasPlannedSeats} />
                   )}
                   {hasReopenDate && (
                     <InfoRow field="reopen-date" label="Reopen Date" value={fmtDate(location.reopenDate)}
                       last={!hasPlannedSeats} />
                  )}
                  {hasPlannedSeats && (
                    <InfoRow field="planned-seats" label="Planned Seats" value={location.plannedSeats} last />
                  )}
                </>
              );
            })()}
          </>
        )}
      </div>
      {/* LSE-MOVE-IN-OUT-DATES: Occupancy section — read-only, derived from
          lease data. Only rendered outside edit mode (occupancy dates live
          on leases, not on the location row). Visually separated from the
          contractual date rows above by a top border + small sub-header.
          Single-lease locations: "Occupied since" / "Vacated on" summary.
          Multi-lease locations (relocations, concurrent leases): per-lease
          rows keyed by lease ID so there is no misleading collapsed summary.
          Hidden entirely when no leases have either date set. */}
      {!inEdit && (() => {
        const leasesWithDates = (location.leases || []).filter(
          (l) => !l.deletedAt && (l.moveInDate != null || l.moveOutDate != null)
        );
        if (leasesWithDates.length === 0) return null;

        if (leasesWithDates.length === 1) {
          const l = leasesWithDates[0];
          const rows = [];
          if (l.moveInDate  != null) rows.push({ key: "in",  label: "Occupied since", value: fmtDate(l.moveInDate),  testId: "occupied-since" });
          if (l.moveOutDate != null) rows.push({ key: "out", label: "Vacated on",      value: fmtDate(l.moveOutDate), testId: "vacated-on"     });
          return (
            <div data-testid="location-details-occupancy" style={{ borderTop: `1px solid ${T.border}`, padding: "0 20px" }}>
              <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: "0.06em", color: T.mid, padding: "10px 0 2px" }}>
                Occupancy
              </div>
              {rows.map((r, i) => (
                <InfoRow key={r.key} field={r.testId} label={r.label} value={r.value} last={i === rows.length - 1} />
              ))}
            </div>
          );
        }

        return (
          <div data-testid="location-details-occupancy" style={{ borderTop: `1px solid ${T.border}`, padding: "0 20px" }}>
            <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: "0.06em", color: T.mid, padding: "10px 0 2px" }}>
              Occupancy
            </div>
            {leasesWithDates.map((l, idx) => {
              const parts = [
                l.moveInDate  ? `In: ${fmtDate(l.moveInDate)}`  : null,
                l.moveOutDate ? `Out: ${fmtDate(l.moveOutDate)}` : null,
              ].filter(Boolean).join(" · ");
              return (
                <InfoRow
                  key={l.id}
                  label={l.id}
                  value={parts}
                  last={idx === leasesWithDates.length - 1}
                />
              );
            })}
          </div>
        );
      })()}
      {/* OFFICE-HEADCOUNT-MANUAL-OVERRIDE: read-only headcount schedule section.
          Shown when the parent passes headcountSchedule with at least one row.
          Gated on !inEdit — the editable instance inside the inEdit branch
          already covers edit mode; this block is read-only-only. */}
      {!inEdit && headcountSchedule != null && headcountSchedule.rows != null && headcountSchedule.rows.length > 0 && (
        <div
          data-testid="location-details-hcsched-readonly"
          style={{ borderTop: `1px solid ${T.border}`, padding: "0 20px 8px" }}
        >
          <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: "0.06em", color: T.mid, padding: "10px 0 4px" }}>
            Headcount Schedule
          </div>
          <HeadcountScheduleSection
            rows={headcountSchedule.rows}
            readOnly
          />
        </div>
      )}
      {/* LD1 — ID reshaped from a muted InfoRow to a small inline
          footer below the data rows. Renders in both read-only and
          edit modes (immutable PK — never part of the editable set). */}
      <div
        data-testid="location-details-id-footer"
        style={{ fontSize: T.fsXs, color: T.light, padding: "8px 20px 12px" }}
      >
        ID: {location.id}
      </div>
    </div>
  );
}

// Task-579 — exported for isolated empty-prop guard tests only.
// Do not use EditableRow in application code outside this module.
export const __testables = { EditableRow };
